/**
 * BrightPath - useExpenseReports Hook
 * BP-04-04: Expenses Frontend
 */

import { useQuery, useMutation } from '@tanstack/react-query';
import { reportsApi } from '../api';
import type {
  TaxReport,
  InsuranceReport,
  CategoryReport,
  ChildReport,
  ReportParams,
  ExportParams,
  ExportFormat,
} from '../types';

// Query keys
export const reportKeys = {
  all: ['expense-reports'] as const,
  tax: (familyId: string, year: number) =>
    [...reportKeys.all, 'tax', familyId, year] as const,
  insurance: (familyId: string, params?: Record<string, unknown>) =>
    [...reportKeys.all, 'insurance', familyId, params] as const,
  category: (familyId: string, params?: Record<string, unknown>) =>
    [...reportKeys.all, 'category', familyId, params] as const,
  child: (familyId: string, params?: Record<string, unknown>) =>
    [...reportKeys.all, 'child', familyId, params] as const,
};

/**
 * Hook for tax-deductible expense report
 */
export function useTaxReport(familyId: string, year: number) {
  return useQuery({
    queryKey: reportKeys.tax(familyId, year),
    queryFn: () => reportsApi.getTaxReport(familyId, year),
    enabled: !!familyId && !!year,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook for insurance claims report
 */
export function useInsuranceReport(
  familyId: string,
  params?: {
    startDate?: string;
    endDate?: string;
    year?: number;
  }
) {
  return useQuery({
    queryKey: reportKeys.insurance(familyId, params),
    queryFn: () => reportsApi.getInsuranceReport(familyId, params),
    enabled: !!familyId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook for category breakdown report
 */
export function useCategoryReport(
  familyId: string,
  params?: {
    startDate?: string;
    endDate?: string;
  }
) {
  return useQuery({
    queryKey: reportKeys.category(familyId, params),
    queryFn: () => reportsApi.getCategoryReport(familyId, params),
    enabled: !!familyId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook for per-child expense report
 */
export function useChildReport(
  familyId: string,
  params?: {
    startDate?: string;
    endDate?: string;
  }
) {
  return useQuery({
    queryKey: reportKeys.child(familyId, params),
    queryFn: () => reportsApi.getChildReport(familyId, params),
    enabled: !!familyId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook for exporting expenses as CSV
 */
export function useExportCsv(familyId: string) {
  return useMutation({
    mutationFn: (params?: {
      startDate?: string;
      endDate?: string;
      category?: string;
      childId?: string;
    }) => reportsApi.exportExpensesCsv(familyId, params),
    onSuccess: (data) => {
      // Auto-download the file
      reportsApi.downloadReport(data.url, data.filename);
    },
  });
}

/**
 * Hook for exporting expenses as PDF
 */
export function useExportPdf(familyId: string) {
  return useMutation({
    mutationFn: (params?: {
      startDate?: string;
      endDate?: string;
      category?: string;
      childId?: string;
      includeCharts?: boolean;
    }) => reportsApi.exportExpensesPdf(familyId, params),
    onSuccess: (data) => {
      reportsApi.downloadReport(data.url, data.filename);
    },
  });
}

/**
 * Hook for exporting tax report
 */
export function useExportTaxReport(familyId: string) {
  return useMutation({
    mutationFn: ({ year, format = 'pdf' }: { year: number; format?: ExportFormat }) =>
      reportsApi.exportTaxReport(familyId, year, format),
    onSuccess: (data) => {
      reportsApi.downloadReport(data.url, data.filename);
    },
  });
}

/**
 * Hook for generating custom reports
 */
export function useGenerateReport(familyId: string) {
  return useMutation({
    mutationFn: (params: ReportParams) => reportsApi.generateReport(familyId, params),
  });
}

/**
 * Hook for exporting with custom parameters
 */
export function useExportReport(familyId: string) {
  return useMutation({
    mutationFn: (params: ExportParams) => reportsApi.exportReport(familyId, params),
    onSuccess: (data) => {
      reportsApi.downloadReport(data.url, data.filename);
    },
  });
}

/**
 * Hook for downloading a report file
 */
export function useDownloadReport() {
  return useMutation({
    mutationFn: ({ url, filename }: { url: string; filename: string }) =>
      reportsApi.downloadReport(url, filename),
  });
}
